import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LOCALE_CONFIG } from '@locale/locale-config.provider';
import { ActionStatus } from '@pixacare/shared';
import { TuiButton, TuiHint, TuiIcon } from '@taiga-ui/core';
import { SyncActionDisplayChild } from '../models/sync-action-display-child';
import { SyncProgressChipComponent } from '../sync-progress-chip/sync-progress-chip.component';
import { computeSyncPercentage } from '../utils/compute-sync-percentage';
import { formatRemainingDuration, getRemainingCount } from '../utils/format-remaining-duration';

@Component({
  selector: 'pxc-sync-center-child',
  templateUrl: './sync-center-child.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TuiIcon, TuiButton, TranslocoDirective, TuiHint, SyncProgressChipComponent, NgTemplateOutlet],
  host: {
    class: 'flex items-center justify-between gap-2',
  },
})
export class SyncCenterChildComponent {
  private readonly localeConfig = inject(LOCALE_CONFIG);
  private readonly translocoService = inject(TranslocoService);
  readonly child = input.required<SyncActionDisplayChild>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly resume = output<void>();
  readonly retry = output<void>();
  readonly delete = output<void>();
  protected readonly ActionStatus = ActionStatus;

  protected readonly percentage = computed(() => computeSyncPercentage(this.child().state.progress));

  protected readonly remainingLabel = computed(() => {
    const { status, remainingSeconds } = this.child().state;
    if (status !== ActionStatus.IN_PROGRESS || remainingSeconds === undefined) {
      return undefined;
    }

    return this.translocoService.translate('{{ duration }} restantes', {
      count: getRemainingCount(remainingSeconds),
      duration: formatRemainingDuration(remainingSeconds, this.localeConfig.code),
    });
  });
}
